import React, { useState, useEffect } from 'react'
import {
  MagnifyingGlassIcon,
  FunnelIcon,
  CurrencyDollarIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  DocumentTextIcon,
  CalendarIcon,
  CreditCardIcon,
  BanknotesIcon,
  ArrowsUpDownIcon,
  ExclamationTriangleIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import { paymentsApi, teacherWithdrawalsApi } from '../../services/adminApi'
import { ChartBarIcon } from '@heroicons/react/24/outline'
import { RechargeConfigManagement } from './RechargeConfigManagement'

interface Payment {
  id: string
  order_number: string
  student_name: string
  student_email: string
  amount: number
  currency: string
  payment_method: string
  status: 'pending' | 'completed' | 'failed' | 'refunded'
  payment_type: 'purchase' | 'refund' | 'other'
  hours_purchased: number
  package_title?: string
  created_at: string
  paid_at?: string
  refunded_at?: string
  refund_reason?: string
}

interface PaymentStats {
  overview: {
    total_payments: number
    completed_payments: number
    pending_payments: number
    failed_payments: number
    refunded_payments: number
    total_revenue: number
    total_refunded: number
    total_hours_sold: number
    // 当日和昨日充值统计
    today_revenue?: number
    today_payments?: number
    yesterday_revenue?: number
    yesterday_payments?: number
  }
  paymentMethods: Array<{
    payment_method: string
    count: number
    revenue: number
    avg_amount: number
    success_rate: number
  }>
  dailyRevenue: Array<{
    date: string
    payments_count: number
    revenue: number
    hours_sold: number
  }>
}

interface TeacherWithdrawal {
  id: number
  teacher_id: number
  teacher_name: string
  teacher_email: string
  amount: number
  method: 'paypal' | 'alipay' | 'wepay'
  account: string
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  rejection_reason?: string
  admin_notes?: string
  processed_by?: number
  processed_at?: string
  created_at: string
  updated_at: string
}

interface EarningsRecord {
  id: number
  booking_id: number
  teacher_id: number
  student_id: number
  student_paid_amount: number
  teacher_earned_amount: number
  platform_profit: number
  student_rate_used: number
  teacher_rate_used: number
  duration_minutes: number
  status: 'pending' | 'frozen' | 'available' | 'withdrawn'
  class_completed_at?: string
  freeze_until?: string
  available_at?: string
  withdrawn_at?: string
  created_at: string
  teacher_name?: string
  teacher_email?: string
  student_name?: string
  student_email?: string
  scheduled_start?: string
  scheduled_end?: string
}

interface EarningsStats {
  overview: {
    total_records: number
    total_student_paid: number
    total_teacher_earned: number
    total_profit: number
    pending_count: number
    frozen_count: number
    available_count: number
    withdrawn_count: number
    frozen_amount: number
    available_amount: number
    withdrawn_amount: number
  }
  dailyStats: Array<{
    date: string
    records_count: number
    student_paid: number
    teacher_earned: number
    profit: number
  }>
}

export const PaymentManagement: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'payments' | 'withdrawals' | 'earnings' | 'recharge-config'>('payments')
  const [payments, setPayments] = useState<Payment[]>([])
  const [stats, setStats] = useState<PaymentStats | null>(null)
  const [loading, setLoading] = useState(false)
  const [selectedPayment, setSelectedPayment] = useState<Payment | null>(null)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [refundReason, setRefundReason] = useState('')

  // 搜索和筛选状态
  const [searchTerm, setSearchTerm] = useState('')
  const [filters, setFilters] = useState({
    status: '',
    payment_method: '',
    type: '',
    start_date: '',
    end_date: ''
  })

  // 分页状态
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  })

  // 教师提现状态
  const [withdrawals, setWithdrawals] = useState<TeacherWithdrawal[]>([])
  const [withdrawalsLoading, setWithdrawalsLoading] = useState(false)
  const [selectedWithdrawal, setSelectedWithdrawal] = useState<TeacherWithdrawal | null>(null)
  const [showApproveModal, setShowApproveModal] = useState(false)
  const [showRejectModal, setShowRejectModal] = useState(false)
  const [adminNotes, setAdminNotes] = useState('')
  const [rejectReason, setRejectReason] = useState('')
  const [withdrawalFilters, setWithdrawalFilters] = useState({
    status: '',
    search: ''
  })
  const [withdrawalPagination, setWithdrawalPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  })

  // 收益记录状态
  const [earningsRecords, setEarningsRecords] = useState<EarningsRecord[]>([])
  const [earningsStats, setEarningsStats] = useState<EarningsStats | null>(null)
  const [earningsLoading, setEarningsLoading] = useState(false)
  const [earningsFilters, setEarningsFilters] = useState({
    status: '',
    search: '',
    start_date: '',
    end_date: ''
  })
  const [earningsPagination, setEarningsPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    hasMore: false
  })

  // 加载支付数据
  const loadPayments = async () => {
    try {
      setLoading(true)
      const response = await paymentsApi.getPayments({
        page: pagination.page,
        limit: pagination.limit,
        search: searchTerm,
        ...filters
      })

      if (response.success) {
        setPayments(response.data)
        setPagination(prev => ({
          ...prev,
          total: response.pagination.total,
          hasMore: response.pagination.hasMore
        }))
      }
    } catch (error) {
      console.error('加载支付数据失败:', error)
      toast.error('加载支付数据失败')
    } finally {
      setLoading(false)
    }
  }

  // 加载统计数据
  const loadStats = async () => {
    try {
      const response = await paymentsApi.getPaymentStats({
        start_date: filters.start_date,
        end_date: filters.end_date
      })

      if (response.success) {
        setStats(response.data)
      }
    } catch (error) {
      console.error('加载统计数据失败:', error)
    }
  }

  // 处理支付状态变更
  const handleStatusChange = async (paymentId: string, newStatus: string, reason?: string) => {
    try {
      const response = await paymentsApi.updatePaymentStatus(paymentId, newStatus, reason)
      if (response.success) {
        toast.success('支付状态已更新')
        loadPayments()
        loadStats()
        if (newStatus === 'refunded') {
          setShowRefundModal(false)
          setRefundReason('')
        }
      }
    } catch (error) {
      console.error('更新支付状态失败:', error)
      toast.error('更新支付状态失败')
    }
  }

  // 导出数据
  const handleExport = async (format: 'csv' | 'json') => {
    try {
      const response = await paymentsApi.exportPayments({
        format,
        ...filters
      })

      if (response.success) {
        // 创建下载链接
        const blob = new Blob([response.data], {
          type: format === 'csv' ? 'text/csv' : 'application/json'
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `payments_export_${new Date().toISOString().split('T')[0]}.${format}`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        window.URL.revokeObjectURL(url)

        toast.success('数据导出成功')
      }
    } catch (error) {
      console.error('导出数据失败:', error)
      toast.error('导出数据失败')
    }
  }

  // 查看支付详情
  const handleViewDetail = async (paymentId: string) => {
    try {
      const response = await paymentsApi.getPaymentDetail(paymentId)
      if (response.success) {
        setSelectedPayment({
          ...response.data.payment,
          student_name: response.data.payment.student_name,
          student_email: response.data.payment.student_email
        })
        setShowDetailModal(true)
      }
    } catch (error) {
      console.error('获取支付详情失败:', error)
      toast.error('获取支付详情失败')
    }
  }

  // 重置筛选条件
  const handleResetFilters = () => {
    setFilters({
      status: '',
      payment_method: '',
      type: '',
      start_date: '',
      end_date: ''
    })
    setSearchTerm('')
    setPagination(prev => ({ ...prev, page: 1 }))
  }

  // 初始化数据
  useEffect(() => {
    loadPayments()
    loadStats()
  }, [pagination.page, searchTerm, filters])

  // 加载教师提现数据
  const loadWithdrawals = async () => {
    try {
      setWithdrawalsLoading(true)
      const response = await teacherWithdrawalsApi.getWithdrawals({
        page: withdrawalPagination.page,
        limit: withdrawalPagination.limit,
        status: withdrawalFilters.status as any || undefined,
        search: withdrawalFilters.search || undefined
      })

      if (response.success) {
        setWithdrawals(response.data?.withdrawals || [])
        setWithdrawalPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          hasMore: response.pagination?.hasMore || false
        }))
      }
    } catch (error) {
      console.error('加载提现数据失败:', error)
      toast.error('加载提现数据失败')
    } finally {
      setWithdrawalsLoading(false)
    }
  }

  // 切换到提现标签时加载数据
  useEffect(() => {
    if (activeTab === 'withdrawals') {
      loadWithdrawals()
    }
  }, [activeTab, withdrawalPagination.page, withdrawalFilters])

  // 加载收益记录
  const loadEarningsRecords = async () => {
    try {
      setEarningsLoading(true)
      const response = await paymentsApi.getEarningsRecords({
        page: earningsPagination.page,
        limit: earningsPagination.limit,
        ...earningsFilters
      })

      if (response.success) {
        setEarningsRecords(response.data || [])
        setEarningsPagination(prev => ({
          ...prev,
          total: response.pagination?.total || 0,
          hasMore: response.pagination?.hasMore || false
        }))
      }
    } catch (error) {
      console.error('加载收益记录失败:', error)
      toast.error('加载收益记录失败')
    } finally {
      setEarningsLoading(false)
    }
  }

  // 加载收益统计
  const loadEarningsStats = async () => {
    try {
      const response = await paymentsApi.getEarningsStats({
        start_date: earningsFilters.start_date,
        end_date: earningsFilters.end_date
      })

      if (response.success) {
        setEarningsStats(response.data)
      }
    } catch (error) {
      console.error('加载收益统计失败:', error)
    }
  }

  // 切换到收益标签时加载数据
  useEffect(() => {
    if (activeTab === 'earnings') {
      loadEarningsRecords()
      loadEarningsStats()
    }
  }, [activeTab, earningsPagination.page, earningsFilters])

  // 获取收益状态样式
  const getEarningsStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-800'
      case 'frozen':
        return 'bg-blue-100 text-blue-800'
      case 'available':
        return 'bg-green-100 text-green-800'
      case 'withdrawn':
        return 'bg-purple-100 text-purple-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取收益状态文本
  const getEarningsStatusText = (status: string) => {
    switch (status) {
      case 'pending':
        return '待完成'
      case 'frozen':
        return '冻结中'
      case 'available':
        return '可提现'
      case 'withdrawn':
        return '已提现'
      default:
        return status
    }
  }

  // 审批提现
  const handleApproveWithdrawal = async () => {
    if (!selectedWithdrawal) return
    try {
      const response = await teacherWithdrawalsApi.approveWithdrawal(selectedWithdrawal.id, adminNotes || undefined)
      if (response.success) {
        toast.success('提现申请已审批')
        setShowApproveModal(false)
        setSelectedWithdrawal(null)
        setAdminNotes('')
        loadWithdrawals()
      }
    } catch (error) {
      console.error('审批失败:', error)
      toast.error('审批失败')
    }
  }

  // 拒绝提现
  const handleRejectWithdrawal = async () => {
    if (!selectedWithdrawal || !rejectReason.trim()) {
      toast.error('请输入拒绝原因')
      return
    }
    try {
      const response = await teacherWithdrawalsApi.rejectWithdrawal(selectedWithdrawal.id, rejectReason.trim(), adminNotes || undefined)
      if (response.success) {
        toast.success('提现申请已拒绝')
        setShowRejectModal(false)
        setSelectedWithdrawal(null)
        setRejectReason('')
        setAdminNotes('')
        loadWithdrawals()
      }
    } catch (error) {
      console.error('拒绝失败:', error)
      toast.error('拒绝失败')
    }
  }

  // 获取提现状态样式
  const getWithdrawalStatusStyle = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'approved':
        return 'bg-green-100 text-green-800'
      case 'rejected':
        return 'bg-red-100 text-red-800'
      case 'completed':
        return 'bg-blue-100 text-blue-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取提现方式显示文本
  const getWithdrawalMethodText = (method: string) => {
    switch (method) {
      case 'paypal':
        return 'PayPal'
      case 'alipay':
        return '支付宝'
      case 'wepay':
        return '微信支付'
      default:
        return method
    }
  }

  // 格式化金额
  const formatAmount = (amount: number, currency: string = 'CNY') => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: currency
    }).format(amount)
  }

  // 格式化日期
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('zh-CN')
  }

  // 获取状态样式
  const getStatusStyle = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-100 text-green-800'
      case 'pending':
        return 'bg-yellow-100 text-yellow-800'
      case 'failed':
        return 'bg-red-100 text-red-800'
      case 'refunded':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  // 获取支付方式图标
  const getPaymentMethodIcon = (method: string) => {
    switch (method) {
      case 'wechat':
        return <BanknotesIcon className="h-5 w-5" />
      case 'alipay':
        return <CreditCardIcon className="h-5 w-5" />
      case 'bank_transfer':
        return <ArrowsUpDownIcon className="h-5 w-5" />
      default:
        return <CurrencyDollarIcon className="h-5 w-5" />
    }
  }

  return (
    <div className="space-y-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">支付管理</h1>
          <p className="mt-2 text-gray-600">管理平台的所有支付交易</p>
        </div>
        {activeTab === 'payments' && (
          <div className="flex space-x-3">
            <button
              onClick={() => handleExport('csv')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <ArrowDownTrayIcon className="h-4 w-4 mr-2" />
              导出CSV
            </button>
            <button
              onClick={() => handleExport('json')}
              className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            >
              <DocumentTextIcon className="h-4 w-4 mr-2" />
              导出JSON
            </button>
          </div>
        )}
      </div>

      {/* 标签切换 */}
      <div className="border-b border-gray-200">
        <nav className="-mb-px flex space-x-8">
          <button
            onClick={() => setActiveTab('payments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'payments'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <CurrencyDollarIcon className="h-5 w-5 inline mr-2" />
            学生充值
          </button>
          <button
            onClick={() => setActiveTab('withdrawals')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'withdrawals'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <BanknotesIcon className="h-5 w-5 inline mr-2" />
            教师提现
          </button>
          <button
            onClick={() => setActiveTab('earnings')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'earnings'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <ChartBarIcon className="h-5 w-5 inline mr-2" />
            课程收益
          </button>
          <button
            onClick={() => setActiveTab('recharge-config')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${activeTab === 'recharge-config'
              ? 'border-purple-500 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
          >
            <CreditCardIcon className="h-5 w-5 inline mr-2" />
            充值配置
          </button>
        </nav>
      </div>

      {activeTab === 'payments' && (
        <>
          {/* 统计卡片 */}
          {stats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">当日充值金额</p>
                    <p className="text-2xl font-bold text-gray-900 mt-1">
                      {formatAmount(stats.overview.today_revenue || 0)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {stats.overview.today_payments || 0} 笔交易
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <CurrencyDollarIcon className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">待处理</p>
                    <p className="text-2xl font-bold text-yellow-600 mt-1">
                      {stats.overview.pending_payments}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">笔交易</p>
                  </div>
                  <div className="p-3 bg-yellow-100 rounded-full">
                    <ClockIcon className="h-6 w-6 text-yellow-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">已退款</p>
                    <p className="text-2xl font-bold text-red-600 mt-1">
                      {formatAmount(stats.overview.total_refunded)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {stats.overview.refunded_payments} 笔
                    </p>
                  </div>
                  <div className="p-3 bg-red-100 rounded-full">
                    <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">昨日充值金额</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      {formatAmount(stats.overview.yesterday_revenue || 0)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {stats.overview.yesterday_payments || 0} 笔交易
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <CalendarIcon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 搜索和筛选 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索订单号、学生邮箱或姓名..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                />
              </div>

              <select
                value={filters.status}
                onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              >
                <option value="">全部状态</option>
                <option value="pending">待处理</option>
                <option value="completed">已完成</option>
                <option value="failed">失败</option>
                <option value="refunded">已退款</option>
              </select>

              <select
                value={filters.payment_method}
                onChange={(e) => setFilters(prev => ({ ...prev, payment_method: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              >
                <option value="">全部支付方式</option>
                <option value="wechat">微信支付</option>
                <option value="alipay">支付宝</option>
                <option value="bank_transfer">银行转账</option>
                <option value="credit_card">信用卡</option>
              </select>

              <select
                value={filters.type}
                onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              >
                <option value="">全部类型</option>
                <option value="purchase">购买</option>
                <option value="refund">退款</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
              <input
                type="date"
                value={filters.start_date}
                onChange={(e) => setFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                placeholder="开始日期"
              />
              <input
                type="date"
                value={filters.end_date}
                onChange={(e) => setFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                placeholder="结束日期"
              />
              <button
                onClick={handleResetFilters}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                重置筛选
              </button>
            </div>
          </div>

          {/* 充值列表 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">充值记录</h3>
              <p className="text-sm text-gray-500 mt-1">
                共 {pagination.total} 条记录，当前第 {pagination.page} 页
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      订单号
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      学生信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      充值金额
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      支付方式
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      充值时间
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {loading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                          <span className="ml-3 text-gray-500">加载中...</span>
                        </div>
                      </td>
                    </tr>
                  ) : payments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        暂无支付记录
                      </td>
                    </tr>
                  ) : (
                    payments.map((payment) => (
                      <tr key={payment.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{payment.order_number}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{payment.student_name}</div>
                            <div className="text-sm text-gray-500">{payment.student_email}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            {formatAmount(payment.amount, payment.currency)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-2">
                            {getPaymentMethodIcon(payment.payment_method)}
                            <span className="text-sm text-gray-900 capitalize">
                              {payment.payment_method.replace('_', ' ')}
                            </span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <select
                            value={payment.status}
                            onChange={(e) => {
                              if (e.target.value === 'refunded') {
                                setSelectedPayment(payment)
                                setShowRefundModal(true)
                              } else {
                                handleStatusChange(payment.id, e.target.value)
                              }
                            }}
                            className={`text-xs font-medium rounded-full border cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500 ${getStatusStyle(payment.status)}`}
                          >
                            <option value="pending">待处理</option>
                            <option value="completed">已完成</option>
                            <option value="failed">失败</option>
                            <option value="refunded">已退款</option>
                          </select>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {payment.paid_at ? formatDate(payment.paid_at) : formatDate(payment.created_at)}
                          </div>
                          {!payment.paid_at && (
                            <div className="text-xs text-yellow-600">待支付</div>
                          )}
                          {payment.refunded_at && (
                            <div className="text-xs text-red-600">退款: {formatDate(payment.refunded_at)}</div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <button
                            onClick={() => handleViewDetail(payment.id)}
                            className="text-purple-600 hover:text-purple-900 mr-3"
                          >
                            <EyeIcon className="h-4 w-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {pagination.total > pagination.limit && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  显示第 {((pagination.page - 1) * pagination.limit) + 1} 到{' '}
                  {Math.min(pagination.page * pagination.limit, pagination.total)} 条，
                  共 {pagination.total} 条记录
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={pagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={!pagination.hasMore}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 支付详情弹窗 */}
          {showDetailModal && selectedPayment && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-2xl w-full mx-4 max-h-screen overflow-y-auto">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">支付详情</h3>
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* 基本信息 */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">基本信息</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">订单号</p>
                        <p className="font-medium">{selectedPayment.order_number}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">支付状态</p>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusStyle(selectedPayment.status)}`}>
                          {selectedPayment.status}
                        </span>
                      </div>
                      <div>
                        <p className="text-gray-500">支付金额</p>
                        <p className="font-medium">{formatAmount(selectedPayment.amount, selectedPayment.currency)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">课时数</p>
                        <p className="font-medium">{selectedPayment.hours_purchased} 小时</p>
                      </div>
                    </div>
                  </div>

                  {/* 学生信息 */}
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-blue-700 mb-2">学生信息</h4>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <p className="text-gray-500">姓名</p>
                        <p className="font-medium">{selectedPayment.student_name}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">邮箱</p>
                        <p className="font-medium">{selectedPayment.student_email}</p>
                      </div>
                    </div>
                  </div>

                  {/* 时间信息 */}
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-700 mb-2">时间信息</h4>
                    <div className="space-y-2 text-sm">
                      <div>
                        <p className="text-gray-500">创建时间</p>
                        <p className="font-medium">{formatDate(selectedPayment.created_at)}</p>
                      </div>
                      {selectedPayment.paid_at && (
                        <div>
                          <p className="text-gray-500">支付时间</p>
                          <p className="font-medium">{formatDate(selectedPayment.paid_at)}</p>
                        </div>
                      )}
                      {selectedPayment.refunded_at && (
                        <div>
                          <p className="text-gray-500">退款时间</p>
                          <p className="font-medium">{formatDate(selectedPayment.refunded_at)}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedPayment.refund_reason && (
                    <div className="bg-red-50 p-4 rounded-lg">
                      <h4 className="text-sm font-medium text-red-700 mb-2">退款原因</h4>
                      <p className="text-sm text-gray-700">{selectedPayment.refund_reason}</p>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    关闭
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 退款确认弹窗 */}
          {showRefundModal && selectedPayment && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">确认退款</h3>
                  <button
                    onClick={() => {
                      setShowRefundModal(false)
                      setRefundReason('')
                      setSelectedPayment(null)
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">退款金额</p>
                    <p className="text-xl font-bold text-red-600">{formatAmount(selectedPayment.amount, selectedPayment.currency)}</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">退款原因</label>
                    <textarea
                      value={refundReason}
                      onChange={(e) => setRefundReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      placeholder="请输入退款原因..."
                      required
                    />
                  </div>

                  <div className="bg-yellow-50 p-3 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <strong>注意：</strong>退款后将自动扣减学生的课时余额，此操作不可撤销。
                    </p>
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowRefundModal(false)
                      setRefundReason('')
                      setSelectedPayment(null)
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={() => {
                      if (refundReason.trim()) {
                        handleStatusChange(selectedPayment.id, 'refunded', refundReason.trim())
                      } else {
                        toast.error('请输入退款原因')
                      }
                    }}
                    disabled={!refundReason.trim()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    确认退款
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 教师提现管理 */}
      {activeTab === 'withdrawals' && (
        <>
          {/* 提现筛选 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索教师姓名或邮箱..."
                  value={withdrawalFilters.search}
                  onChange={(e) => setWithdrawalFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                />
              </div>
              <select
                value={withdrawalFilters.status}
                onChange={(e) => setWithdrawalFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              >
                <option value="">全部状态</option>
                <option value="pending">待处理</option>
                <option value="approved">已审批</option>
                <option value="rejected">已拒绝</option>
                <option value="completed">已完成</option>
              </select>
              <button
                onClick={() => setWithdrawalFilters({ status: '', search: '' })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                重置筛选
              </button>
            </div>
          </div>

          {/* 提现列表 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">教师提现申请</h3>
              <p className="text-sm text-gray-500 mt-1">
                共 {withdrawalPagination.total} 条记录
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      教师信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      提现金额
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      提现方式
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      收款账号
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      申请时间
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      操作
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {withdrawalsLoading ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                          <span className="ml-3 text-gray-500">加载中...</span>
                        </div>
                      </td>
                    </tr>
                  ) : withdrawals.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-500">
                        暂无提现申请
                      </td>
                    </tr>
                  ) : (
                    withdrawals.map((withdrawal) => (
                      <tr key={withdrawal.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{withdrawal.teacher_name || '未知'}</div>
                            <div className="text-sm text-gray-500">{withdrawal.teacher_email || ''}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">
                            ${withdrawal.amount?.toFixed(2) || '0.00'}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {getWithdrawalMethodText(withdrawal.method)}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900 truncate max-w-[150px]" title={withdrawal.account}>
                            {withdrawal.account}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getWithdrawalStatusStyle(withdrawal.status)}`}>
                            {withdrawal.status === 'pending' && '待处理'}
                            {withdrawal.status === 'approved' && '已审批'}
                            {withdrawal.status === 'rejected' && '已拒绝'}
                            {withdrawal.status === 'completed' && '已完成'}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">{formatDate(withdrawal.created_at)}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          {withdrawal.status === 'pending' && (
                            <div className="flex justify-end space-x-2">
                              <button
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal)
                                  setShowApproveModal(true)
                                }}
                                className="text-green-600 hover:text-green-900"
                                title="审批"
                              >
                                <CheckIcon className="h-5 w-5" />
                              </button>
                              <button
                                onClick={() => {
                                  setSelectedWithdrawal(withdrawal)
                                  setShowRejectModal(true)
                                }}
                                className="text-red-600 hover:text-red-900"
                                title="拒绝"
                              >
                                <XMarkIcon className="h-5 w-5" />
                              </button>
                            </div>
                          )}
                          {withdrawal.status !== 'pending' && (
                            <span className="text-gray-400">已处理</span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {withdrawalPagination.total > withdrawalPagination.limit && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  显示第 {((withdrawalPagination.page - 1) * withdrawalPagination.limit) + 1} 到{' '}
                  {Math.min(withdrawalPagination.page * withdrawalPagination.limit, withdrawalPagination.total)} 条，
                  共 {withdrawalPagination.total} 条记录
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setWithdrawalPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={withdrawalPagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setWithdrawalPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={!withdrawalPagination.hasMore}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* 审批弹窗 */}
          {showApproveModal && selectedWithdrawal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">确认审批提现</h3>
                  <button
                    onClick={() => {
                      setShowApproveModal(false)
                      setSelectedWithdrawal(null)
                      setAdminNotes('')
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-green-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">教师</p>
                    <p className="font-medium">{selectedWithdrawal.teacher_name}</p>
                    <p className="text-sm text-gray-500">{selectedWithdrawal.teacher_email}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">提现金额</p>
                    <p className="text-xl font-bold text-green-600">${selectedWithdrawal.amount?.toFixed(2)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">提现方式: {getWithdrawalMethodText(selectedWithdrawal.method)}</p>
                    <p className="text-sm text-gray-600">收款账号: {selectedWithdrawal.account}</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">管理员备注 (可选)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      placeholder="输入备注..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowApproveModal(false)
                      setSelectedWithdrawal(null)
                      setAdminNotes('')
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleApproveWithdrawal}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
                  >
                    确认审批
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* 拒绝弹窗 */}
          {showRejectModal && selectedWithdrawal && (
            <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">拒绝提现申请</h3>
                  <button
                    onClick={() => {
                      setShowRejectModal(false)
                      setSelectedWithdrawal(null)
                      setRejectReason('')
                      setAdminNotes('')
                    }}
                    className="text-gray-400 hover:text-gray-500"
                  >
                    <XCircleIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div className="bg-red-50 p-4 rounded-lg">
                    <p className="text-sm text-gray-600">教师</p>
                    <p className="font-medium">{selectedWithdrawal.teacher_name}</p>
                    <p className="text-sm text-gray-600">提现金额: <span className="font-bold">${selectedWithdrawal.amount?.toFixed(2)}</span></p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">拒绝原因 (必填)</label>
                    <textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      placeholder="请输入拒绝原因..."
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">管理员备注 (可选)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                      placeholder="输入备注..."
                    />
                  </div>
                </div>

                <div className="mt-6 flex justify-end space-x-3">
                  <button
                    onClick={() => {
                      setShowRejectModal(false)
                      setSelectedWithdrawal(null)
                      setRejectReason('')
                      setAdminNotes('')
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                  >
                    取消
                  </button>
                  <button
                    onClick={handleRejectWithdrawal}
                    disabled={!rejectReason.trim()}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    确认拒绝
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* 课程收益记录 */}
      {activeTab === 'earnings' && (
        <>
          {/* 收益统计卡片 */}
          {earningsStats && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">学生总收费 (人民币)</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      ¥{earningsStats.overview.total_student_paid?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      {earningsStats.overview.total_records || 0} 笔课程
                    </p>
                  </div>
                  <div className="p-3 bg-blue-100 rounded-full">
                    <CurrencyDollarIcon className="h-6 w-6 text-blue-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">教师总付费 (美元)</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      ${earningsStats.overview.total_teacher_earned?.toFixed(2) || '0.00'}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      折合人民币: ¥{((earningsStats.overview.total_teacher_earned || 0) * 7).toFixed(2)}
                    </p>
                  </div>
                  <div className="p-3 bg-purple-100 rounded-full">
                    <BanknotesIcon className="h-6 w-6 text-purple-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">平台总利润 (人民币)</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      ¥{((earningsStats.overview.total_student_paid || 0) - (earningsStats.overview.total_teacher_earned || 0) * 7).toFixed(2)}
                    </p>
                    <p className="text-sm text-gray-500 mt-2">
                      利润率: {earningsStats.overview.total_student_paid > 0
                        ? ((((earningsStats.overview.total_student_paid || 0) - (earningsStats.overview.total_teacher_earned || 0) * 7) / earningsStats.overview.total_student_paid) * 100).toFixed(1)
                        : '0'}%
                    </p>
                  </div>
                  <div className="p-3 bg-green-100 rounded-full">
                    <ChartBarIcon className="h-6 w-6 text-green-600" />
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg shadow p-6">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-gray-600">收益状态分布</p>
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-500">待完成: {earningsStats.overview.pending_count || 0}</p>
                      <p className="text-xs text-blue-600">冻结中: {earningsStats.overview.frozen_count || 0}</p>
                      <p className="text-xs text-green-600">可提现: {earningsStats.overview.available_count || 0}</p>
                      <p className="text-xs text-purple-600">已提现: {earningsStats.overview.withdrawn_count || 0}</p>
                    </div>
                  </div>
                  <div className="p-3 bg-gray-100 rounded-full">
                    <ClockIcon className="h-6 w-6 text-gray-600" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* 收益筛选 */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
              <div className="relative">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="搜索教师或学生..."
                  value={earningsFilters.search}
                  onChange={(e) => setEarningsFilters(prev => ({ ...prev, search: e.target.value }))}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                />
              </div>
              <select
                value={earningsFilters.status}
                onChange={(e) => setEarningsFilters(prev => ({ ...prev, status: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
              >
                <option value="">全部状态</option>
                <option value="pending">待完成</option>
                <option value="frozen">冻结中</option>
                <option value="available">可提现</option>
                <option value="withdrawn">已提现</option>
              </select>
              <input
                type="date"
                value={earningsFilters.start_date}
                onChange={(e) => setEarningsFilters(prev => ({ ...prev, start_date: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                placeholder="开始日期"
              />
              <input
                type="date"
                value={earningsFilters.end_date}
                onChange={(e) => setEarningsFilters(prev => ({ ...prev, end_date: e.target.value }))}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 focus:outline-none"
                placeholder="结束日期"
              />
              <button
                onClick={() => setEarningsFilters({ status: '', search: '', start_date: '', end_date: '' })}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                重置筛选
              </button>
            </div>
          </div>

          {/* 收益记录列表 */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">课程收益明细</h3>
              <p className="text-sm text-gray-500 mt-1">
                共 {earningsPagination.total} 条记录，显示学生收费、教师付费、平台利润详情
              </p>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      课程信息
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      教师
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      学生
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-blue-50">
                      学生收费 (¥)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-purple-50">
                      教师付费 ($)
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50">
                      平台利润 (¥)
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      状态
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      完成时间
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {earningsLoading ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center">
                        <div className="flex items-center justify-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                          <span className="ml-3 text-gray-500">加载中...</span>
                        </div>
                      </td>
                    </tr>
                  ) : earningsRecords.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-6 py-12 text-center text-gray-500">
                        暂无收益记录
                      </td>
                    </tr>
                  ) : (
                    earningsRecords.map((record) => (
                      <tr key={record.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">预约 #{record.booking_id}</div>
                            <div className="text-xs text-gray-500">时长: {record.duration_minutes} 分钟</div>
                            <div className="text-xs text-gray-400">
                              费率: 学生 ¥{record.student_rate_used}/25分钟 | 教师 ${record.teacher_rate_used}/25分钟
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{record.teacher_name || '未知'}</div>
                            <div className="text-xs text-gray-500">{record.teacher_email || ''}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div>
                            <div className="text-sm font-medium text-gray-900">{record.student_name || '未知'}</div>
                            <div className="text-xs text-gray-500">{record.student_email || ''}</div>
                          </div>
                        </td>
                        <td className="px-6 py-4 bg-blue-50 text-right">
                          <div className="text-sm font-bold text-blue-600">
                            ¥{Number(record.student_paid_amount || 0).toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 bg-purple-50 text-right">
                          <div className="text-sm font-bold text-purple-600">
                            ${Number(record.teacher_earned_amount || 0).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-400">
                            折合¥{(Number(record.teacher_earned_amount || 0) * 7).toFixed(2)}
                          </div>
                        </td>
                        <td className="px-6 py-4 bg-green-50 text-right">
                          <div className="text-sm font-bold text-green-600">
                            ¥{((record.student_paid_amount || 0) - (record.teacher_earned_amount || 0) * 7).toFixed(2)}
                          </div>
                          <div className="text-xs text-gray-400">
                            {record.student_paid_amount > 0
                              ? ((((record.student_paid_amount || 0) - (record.teacher_earned_amount || 0) * 7) / record.student_paid_amount) * 100).toFixed(1)
                              : '0'}%
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getEarningsStatusStyle(record.status)}`}>
                            {getEarningsStatusText(record.status)}
                          </span>
                          {record.status === 'frozen' && record.freeze_until && (
                            <div className="text-xs text-gray-400 mt-1">
                              解冻: {formatDate(record.freeze_until).split(' ')[0]}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {record.class_completed_at ? formatDate(record.class_completed_at) : '-'}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* 分页 */}
            {earningsPagination.total > earningsPagination.limit && (
              <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                <div className="text-sm text-gray-700">
                  显示第 {((earningsPagination.page - 1) * earningsPagination.limit) + 1} 到{' '}
                  {Math.min(earningsPagination.page * earningsPagination.limit, earningsPagination.total)} 条，
                  共 {earningsPagination.total} 条记录
                </div>
                <div className="flex space-x-2">
                  <button
                    onClick={() => setEarningsPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                    disabled={earningsPagination.page === 1}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    上一页
                  </button>
                  <button
                    onClick={() => setEarningsPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={!earningsPagination.hasMore}
                    className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    下一页
                  </button>
                </div>
              </div>
            )}
          </div>
        </>
      )}

      {/* 充值配置管理 */}
      {activeTab === 'recharge-config' && (
        <RechargeConfigManagement />
      )}
    </div>
  )
}